import { z } from "zod";

/**
 * Permissions (decision 42).
 *
 * An admin's permissions come from a role template — a set of choices, one
 * per module and one per kind of sensitive data — with optional personal
 * overrides on top. The owner (super_admin) is not subject to any of this.
 * Everything here is shared so the worker enforces exactly what the panel
 * shows as ticked.
 *
 * Each list is ordered from least to most: comparing positions is how the
 * worker checks "at least view", and how it stops someone who may manage
 * admins from granting more than they have.
 */

export const MODULE_LEVELS = {
  overview: ["none", "view"],
  players: ["none", "view", "ban"],
  sources: ["none", "view", "manage"],
  broadcasts: ["none", "view", "draft", "send"],
  autoReplies: ["none", "view", "manage"],
  commands: ["none", "view", "manage"],
  footer: ["none", "view", "manage"],
  groups: ["none", "view", "manage"],
  import: ["none", "use"],
  systemLog: ["none", "view"],
  auditLog: ["none", "view"],
  // Account-wide rather than per bot.
  bots: ["none", "view", "manage"],
  admins: ["none", "view", "manage"],
} as const;

export const DATA_LEVELS = {
  playerName: ["hidden", "masked", "full"],
  telegramId: ["hidden", "masked", "full"],
  username: ["hidden", "masked", "full"],
  messageContent: ["hidden", "full"],
  adminEmail: ["masked", "full"],
} as const;

export type Module = keyof typeof MODULE_LEVELS;
export type DataField = keyof typeof DATA_LEVELS;
export type ModuleLevel<M extends Module = Module> = (typeof MODULE_LEVELS)[M][number];
export type DataLevel<F extends DataField = DataField> = (typeof DATA_LEVELS)[F][number];

/** Modules that are about the whole account, not one bot. */
export const ACCOUNT_MODULES: readonly Module[] = ["bots", "admins"];

export interface Permissions {
  modules: { [M in Module]: ModuleLevel<M> };
  data: { [F in DataField]: DataLevel<F> };
}

const L = MODULE_LEVELS;
const D = DATA_LEVELS;

const moduleShape = {
  overview: z.enum(L.overview),
  players: z.enum(L.players),
  sources: z.enum(L.sources),
  broadcasts: z.enum(L.broadcasts),
  autoReplies: z.enum(L.autoReplies),
  commands: z.enum(L.commands),
  footer: z.enum(L.footer),
  groups: z.enum(L.groups),
  import: z.enum(L.import),
  systemLog: z.enum(L.systemLog),
  auditLog: z.enum(L.auditLog),
  bots: z.enum(L.bots),
  admins: z.enum(L.admins),
};

const dataShape = {
  playerName: z.enum(D.playerName),
  telegramId: z.enum(D.telegramId),
  username: z.enum(D.username),
  messageContent: z.enum(D.messageContent),
  adminEmail: z.enum(D.adminEmail),
};

export const permissionsSchema = z.object({
  modules: z.object(moduleShape),
  data: z.object(dataShape),
});

export const overridesSchema = z.object({
  modules: z.object(moduleShape).partial().optional(),
  data: z.object(dataShape).partial().optional(),
});

/** A personal exception to a role: only the choices that differ. */
export type PermissionOverrides = z.infer<typeof overridesSchema>;

/** Everything, for the owner. */
export const ALL_PERMISSIONS: Permissions = {
  modules: Object.fromEntries(
    Object.entries(MODULE_LEVELS).map(([m, levels]) => [m, levels.at(-1)]),
  ) as Permissions["modules"],
  data: Object.fromEntries(
    Object.entries(DATA_LEVELS).map(([f, levels]) => [f, levels.at(-1)]),
  ) as Permissions["data"],
};

/** The built-in "Admin" template: what the old admin role could do. */
export const ADMIN_TEMPLATE: Permissions = {
  modules: { ...ALL_PERMISSIONS.modules, bots: "none", admins: "none" },
  data: { ...ALL_PERMISSIONS.data },
};

/** The built-in "Viewer" template: read-only, no message content. */
export const VIEWER_TEMPLATE: Permissions = {
  modules: {
    overview: "view",
    players: "view",
    sources: "view",
    broadcasts: "view",
    autoReplies: "view",
    commands: "view",
    footer: "view",
    groups: "view",
    import: "none",
    systemLog: "view",
    auditLog: "view",
    bots: "none",
    admins: "none",
  },
  data: { ...ALL_PERMISSIONS.data, messageContent: "hidden" },
};

/** Only the choices an override actually sets; an unset one keeps the role's. */
function setOnly<T extends object>(partial: T | undefined): Partial<T> {
  return Object.fromEntries(
    Object.entries(partial ?? {}).filter(([, value]) => value !== undefined),
  ) as Partial<T>;
}

export function applyOverrides(
  base: Permissions,
  overrides: PermissionOverrides | null,
): Permissions {
  // setOnly drops unset keys, so every key here is set.
  return {
    modules: { ...base.modules, ...setOnly(overrides?.modules) } as Permissions["modules"],
    data: { ...base.data, ...setOnly(overrides?.data) } as Permissions["data"],
  };
}

function rank<L extends string>(levels: readonly L[], level: L): number {
  return levels.indexOf(level);
}

/** True when `have` is at least `need` for this module. */
export function allows<M extends Module>(
  permissions: Permissions,
  module: M,
  need: ModuleLevel<M>,
): boolean {
  const levels = MODULE_LEVELS[module] as readonly string[];
  return rank(levels, permissions.modules[module]) >= rank(levels, need);
}

/**
 * True when every choice in `candidate` is at most the same choice in
 * `limit`. Someone who may manage admins can hand out no more than they
 * hold themselves.
 */
export function withinLimit(candidate: Permissions, limit: Permissions): boolean {
  for (const m of Object.keys(MODULE_LEVELS) as Module[]) {
    const levels = MODULE_LEVELS[m] as readonly string[];
    if (rank(levels, candidate.modules[m]) > rank(levels, limit.modules[m])) return false;
  }
  for (const f of Object.keys(DATA_LEVELS) as DataField[]) {
    const levels = DATA_LEVELS[f] as readonly string[];
    if (rank(levels, candidate.data[f]) > rank(levels, limit.data[f])) return false;
  }
  return true;
}

/**
 * Masks a value for someone allowed to know it exists but not what it is:
 * the first two characters, then ***. Short values keep one.
 */
export function maskText(value: string): string {
  const chars = [...value];
  const keep = chars.length > 3 ? 2 : 1;
  return `${chars.slice(0, keep).join("")}***`;
}

/** An email masked in its local part only: op***@stardynastygrp.com. */
export function maskEmail(email: string): string {
  const at = email.lastIndexOf("@");
  if (at <= 0) return maskText(email);
  return `${maskText(email.slice(0, at))}${email.slice(at)}`;
}

/** Applies a data level to one value: shown, masked, or left out. */
export function reveal(value: string | null, level: "hidden" | "masked" | "full"): string | null {
  if (value === null || value === "") return value;
  if (level === "full") return value;
  if (level === "masked") return maskText(value);
  return null;
}
